import { Button, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { Check, Columns3, Settings2 } from 'lucide-react';
import { useState } from 'react';
import type { ColumnVisibility } from '@/hooks/useColumnVisibility';
import type { ColumnMeta } from '@/lib/tableColumns';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  THE COLUMNS MENU. ONE OF THEM, FOR EVERY LIST IN THE APP. [UX-FINAL]
 *
 *  ⚠️ IT IS GIVEN THE UNFILTERED ARRAY, AND THAT IS THE WHOLE TRICK. The menu has to name
 *  the columns that are OFF, so it cannot be handed the same array the `<Table>` gets. A page
 *  builds ONE array, hands it here, and hands the filtered copy to the table — so no page
 *  keeps a second list of keys or labels. `lib/tableColumns.ts` derives the locks.
 *
 *  ⚠️ `trigger={['click']}`, ALWAYS. A `Dropdown` with no `trigger` opens on HOVER and is
 *  keyboard-unreachable — measured at P4-UX on the instruction line's ⋯: click 0, Enter 0,
 *  Space 0, hover 1. CLAUDE.md §8 requires every Dropdown here to state its trigger.
 *
 *  ⚠️ IT STAYS OPEN ACROSS TOGGLES, which is why `open` is controlled. AntD closes a menu on
 *  item click by default; hiding four columns would then be four round trips to the same
 *  button. Escape and an outside click still close it through `onOpenChange`, so the overlay
 *  sweep's `close: escapeKey` needs nothing special.
 *
 *  ⚠️ NO HAND-DRAWN TICK. `pages/OrgTree` used a tick glyph and an ideographic space inside a
 *  template string, which puts the STATE into the accessible name as a glyph nothing reads
 *  aloud and nothing can query. Here the check is `aria-hidden` decoration, an `invisible`
 *  placeholder keeps the labels aligned, and the state is an `sr-only` WORD — so the name is
 *  "Rank shown" / "Rank hidden", which announces and is findable by `getByRole('menuitem')`.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

const RESET_KEY = '__reset';

const labelOf = (column: ColumnMeta, labels: Readonly<Record<string, string>> | undefined): string => {
  const key = String(column.key ?? '');
  const override = labels?.[key];
  if (override !== undefined) return override;
  if (typeof column.title === 'string' && column.title.trim() !== '') return column.title;
  // A ReactNode title has no text this component can read, and walking its children would
  // break the moment a header carries a count or an icon. Naming the key is honest and still
  // findable; `labels` is how a page gives that column a word.
  return key;
};

export type ColumnsMenuProps<K extends string> = {
  /** ⚠️ THE UNFILTERED ARRAY — the same one `columnKeySet` was given. */
  columns: readonly ColumnMeta[];
  visibility: ColumnVisibility<K>;
  /** From `columnKeySet(...).locked`. These are not offered at all, not offered-disabled. */
  locked: readonly string[];
  /** ONLY for a column whose `title` is not a string. Never a second list. */
  labels?: Readonly<Record<string, string>>;
  /**
   * ⚠️ WHAT THIS MENU IS FOR, when a screen carries two of them.
   *
   * `/org-tree` renders a list AND a detail panel with its own table, so without this both
   * triggers would be named "Columns" — a duplicate accessible name, which breaks
   * `getByRole('button', { name: /columns/i })` and is the failure the repo already
   * legislates against ("must NEVER reuse the labels Edit or Transfer"). The visible text
   * stays "Columns"; only the accessible name gains the qualifier.
   */
  qualifier?: string;
  /** Distinct per menu when a page has two — Playwright's strict mode counts them. */
  testId?: string;
  /**
   * ⚠️ [C3c.2] THE TRIGGER'S SHAPE, AND THE DEFAULT IS THE ONE NINE PAGES ALREADY RENDER.
   *
   * `'button'` is the labelled `Columns` button this component shipped with. `'icon'` is a
   * settings gear for a page that wants the control in its ACTION CLUSTER rather than in the
   * filter row, where a full-width button was taking a line of its own.
   *
   * ⚠️ IT IS A VARIANT ON THE ONE COMPONENT, NEVER A SECOND MENU. `census.spec.ts` greps
   * `<Dropdown` PER FILE and `e2e/fixtures/overlays.ts` carries ONE entry for all ten menus
   * precisely because a consuming page contains no Dropdown of its own — a gear built in
   * `pages/Arrangements/index.tsx` would be a third `<Dropdown` in a file EXEMPT at one with
   * a second swept, and the census would fail on it. Keeping the overlay here costs no
   * bookkeeping at all.
   *
   * ⚠️ AND THE ITEMS DIFFER BY EXACTLY ONE GROUP HEADING, which is not two shapes for the
   * sake of it: the labelled trigger already says `Columns`, so a heading under it would say
   * the same word twice — the "a heading over a labelled field says it twice" rule [D4.2]
   * records. The gear says nothing, so the heading is what names what the menu is FOR.
   */
  variant?: 'button' | 'icon';
  className?: string;
};

export const ColumnsMenu = <K extends string>({
  columns,
  visibility,
  locked,
  labels,
  qualifier,
  testId = 'columns-menu',
  variant = 'button',
  className,
}: ColumnsMenuProps<K>) => {
  const [open, setOpen] = useState(false);
  const lockedSet = new Set<string>(locked);
  const hiddenCount = visibility.hiddenKeys.length;

  const toggles = [
    ...columns
      .filter((column) => column.key !== undefined && !lockedSet.has(String(column.key)))
      .map((column) => {
        const key = String(column.key);
        const shown = visibility.isVisible(key as K);
        return {
          key,
          // ⚠️ PER-ITEM `onClick`, which is the shape `pages/OrgTree` already proved here.
          // A menu-level `onClick` on a controlled `Dropdown` did not fire at all.
          onClick: () => visibility.toggle(key as K),
          label:
            (
              /*
               * ⚠️ THE WHOLE NAME IS ONE `sr-only` TEXT NODE, and the visible half is
               * `aria-hidden`. MEASURED: `computeAccessibleName` trims each text node before
               * joining, so a label span beside a state span produces "Rankshown" — one word,
               * announced wrong and unqueryable — and a leading space inside the string does
               * not survive. Building the phrase once is the only way to control it exactly.
               */
              <span className="flex items-center gap-2">
                <Check className={cn('size-4 shrink-0', shown ? '' : 'invisible')} aria-hidden />
                <span aria-hidden>{labelOf(column, labels)}</span>
                <span className="sr-only">{`${labelOf(column, labels)} ${shown ? 'shown' : 'hidden'}`}</span>
              </span>
            ),
        };
      }),
  ];

  const items: MenuProps['items'] = [
    /*
     * ⚠️ A GROUP, NEVER A SUBMENU. A `children` entry in rc-menu is a SUBMENU, and a submenu
     * opens on HOVER unless `triggerSubMenuAction` is set — the defect CLAUDE.md §8 records
     * for a `Dropdown` with no `trigger`, one layer in. It would also put the toggles behind a
     * second open state, so `ColumnsMenu.test.tsx`'s `getByRole('menuitem', { name: /Phone/ })`
     * would resolve to nothing until something opened it. A group renders INLINE with a
     * heading: one click to reach a toggle, and every existing query still matches.
     */
    ...(variant === 'icon' ? [{ type: 'group' as const, label: 'Columns', children: toggles }] : toggles),
    { type: 'divider' as const },
    {
      key: RESET_KEY,
      /*
       * ⚠️ ALWAYS ENABLED, AND `disabled` IS NOT SAFE HERE. MEASURED: rc-menu caches an
       * item's `disabled` from the first render of the overlay and does not refresh it when
       * the items array changes, so `disabled: hiddenCount === 0` left Reset greyed out FOR
       * EVER the moment an operator hid their first column — the one control they need at
       * exactly that point, permanently dead, with the menu otherwise working.
       *
       * A Reset that no-ops when nothing is hidden costs nothing. The "have I hidden
       * anything?" signal it was carrying lives on the trigger instead, which says
       * "(2 hidden)" and is visible without opening the menu at all.
       */
      /*
       * ⚠️ IT DOES NOT CLOSE THE MENU, and that is not only for consistency with the toggles.
       * Closing in the same handler raced the URL write and the reset silently did nothing —
       * the columns came back in neither store. Leaving it open is also the better behaviour:
       * the operator sees every tick return, which is the confirmation the action needs.
       */
      onClick: () => visibility.reset(),
      label: 'Reset to default',
    },
  ];

  /*
   * ⚠️ THE WHOLE ACCESSIBLE NAME, BUILT ONCE, BECAUSE `aria-label` REPLACES CONTENT RATHER THAN
   * JOINING IT. The icon trigger has no text of its own, so the qualifier and the count have
   * nowhere else to be announced from — and an `sr-only` span inside a button that also carries
   * an `aria-label` is simply not read.
   */
  const name = [
    'Columns',
    qualifier === undefined ? '' : ` — ${qualifier}`,
    hiddenCount > 0 ? ` (${hiddenCount} hidden)` : '',
  ].join('');

  return (
    <Dropdown open={open} onOpenChange={setOpen} trigger={['click']} placement="bottomRight" menu={{ items }}>
      {variant === 'icon' ? (
        /*
         * ⚠️ THE COUNT SURVIVES THE LABEL GOING, AND THAT IS NOT DECORATION. The Reset item is
         * deliberately always enabled (see the Reset item ABOVE), and this file's own argument for that is
         * that the "have I hidden anything?" signal lives on the TRIGGER instead — "visible
         * without opening the menu at all". A bare gear would have destroyed the one hint that
         * a column is MISSING rather than never having existed, which is exactly the state an
         * operator cannot otherwise diagnose. The bare numeral is the whole of it; the word is
         * in the accessible name.
         *
         * ⚠️ A NATIVE `title`, NEVER A `<Tooltip>`. `pages/Arrangements/index.tsx` is EXEMPT at
         * five Tooltips and this component at none, so either home would fail `census.spec.ts`
         * — and #82 measured an AntD Tooltip per row at 64s to 119s. `AddresseeCodes` set the
         * precedent for hover text that costs no overlay.
         */
        <Button
          className={className}
          icon={<Settings2 className="size-4" aria-hidden />}
          aria-label={name}
          title={name}
          data-testid={testId}
        >
          {hiddenCount > 0 && (
            <span className="text-xs text-muted-foreground" aria-hidden>
              {hiddenCount}
            </span>
          )}
        </Button>
      ) : (
        <Button className={className} icon={<Columns3 className="size-4" aria-hidden />} data-testid={testId}>
          Columns
          {qualifier !== undefined && <span className="sr-only"> — {qualifier}</span>}
          {/* The count is the only hint that a column is missing rather than never existing. */}
          {hiddenCount > 0 && <span className="ml-1 text-xs text-muted-foreground">({hiddenCount} hidden)</span>}
        </Button>
      )}
    </Dropdown>
  );
};
